import React, { useCallback, useEffect, useState } from 'react';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';
import { AlertBar, Button, Icon, StsIconName, Typography } from '@packages/ui/shared';
import { analyticsService, cidCvnService } from '@web/services/singletons';
import { CidCvnTable } from '../components/cid-cvn-table/cid-cvn-table';
import { CidCvnCheckResult } from '@packages/models/api/cid-cvn';
import { createUseStyles } from 'react-jss';
import { ReferenceDataLastUpdated } from '../components/reference-data-last-updated';
import { generateCidCvnPdf } from '../cid-cvn-pdf/cid-cvn.pdf';
import { getCidCvnPdfOptions } from '../cid-cvn-pdf/cid-cvn-pdf-helpers';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ScanImageModal } from '../components/scan-image-modal';
import { ToasterType } from '@web/components/toast-notification';
import { useAuthState } from '@packages/contexts/auth';
import { useCidCvn, useCidCvnState } from '@packages/contexts/cid-cvn';
import { useGoogleAnalytics } from '@web/core/google-analytics';
import { useTranslation } from 'react-i18next';
import { useVinSessionState } from '../../vin-sessions/context/vin-sessions.hooks';
import colors from '@packages/core/styles/colors';
import ToastNotification from '@web/components/toast-notification';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics/google-analytics.events';

const logger = getLogger('cid-cvn');

const useStyles = createUseStyles({
    tooltip: {
        width: 272,
        '& .tooltip-inner': {
            backgroundColor: colors.brandSecondary,
            borderRadius: 4,
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: '16px',
            maxWidth: 272,
            padding: '4px 8px',
            textAlign: 'left',
            width: 272,
        },
    },
    supportTooltip: {
        maxWidth: 250,
        '& .tooltip-inner': {
            backgroundColor: colors.graySeven,
            borderRadius: 4,
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '20px',
            padding: '3px 8px',
            textAlign: 'center',
            width: 'max-content',
            maxWidth: 250,
            whiteSpace: 'normal',
        },
    },
});

type ToastState = {
    show: boolean;
    message: string;
    iconName?: StsIconName;
    variant?: ToasterType;
};

export function CidCvnCheck() {
    const classes = useStyles();
    const { account, retailers: accountRetailers } = useAuthState();
    const { googleAnalyticsService } = useGoogleAnalytics();
    const { cidCvnBlocked } = useCidCvnState();
    const { t } = useTranslation();
    const { cidCvnEligible, cidCvnList, errors, isLoading } = useCidCvnState();
    const { clearErrors, fetchCidCvnData, setSelectedReportHeader } = useCidCvn(cidCvnService);
    const [downloadingIds, setDownloadingIds] = useState<{ [id: string]: boolean }>({});
    const [retryingIds, setRetryingIds] = useState<{ [id: string]: boolean }>({});
    const [retryCountsById, setRetryCountsById] = useState<{ [id: string]: number }>({});
    const [modalStep, setModalStep] = useState<'selectRetailer' | 'upload' | 'confirm' | ''>('');
    const [showToast, setShowToast] = useState<ToastState>({ show: false, message: '' });
    const [referenceDataLastUpdated, setReferenceDataLastUpdated] = useState<string | null>(null);
    const { selectedVin } = useVinSessionState();
    const accountFirstandLastName = `${account?.firstName} ${account?.lastName}`;
    const vin = selectedVin?.vinRecord.vin;
    const modelYear = selectedVin?.vinRecord.modelYear;

    const canPerformCidCvnCheck = !!account?.cidCvnCapabilities?.canPerformCIDCVNCheck;
    const canViewCidCvnResults = !!account?.cidCvnCapabilities?.canViewCIDCVNResults;

    const canViewTechlineCase = !!account?.techlineCapabilities?.canViewTechlineCase;
    const canViewTechlineDraftCase = !!account?.techlineCapabilities?.canViewDraftCase;

    const handleDownload = useCallback(
        async (id: string) => {
            if (!id) {
                setShowToast({
                    show: true,
                    message: t('cidCvnCheck:report.downloadErrorMissingId'),
                    iconName: 'x-close',
                    variant: 'error',
                });
                return;
            }
            setDownloadingIds((prev) => ({ ...prev, [id]: true }));
            try {
                const response = await cidCvnService.getCidCvnReport(id);
                if (response.success && response.data) {
                    const pdfOptions = getCidCvnPdfOptions(response.data, t);
                    if (pdfOptions) {
                        generateCidCvnPdf(pdfOptions);
                        setShowToast({
                            show: true,
                            message: t('cidCvnCheck:report.downloadSuccessMessage'),
                        });
                    }
                } else {
                    setShowToast({
                        show: true,
                        message: t('cidCvnCheck:report.downloadErrorMessage'),
                        iconName: 'x-close',
                        variant: 'error',
                    });
                }
            } catch (error) {
                logger.error(`Failed to download the CID/CVN report PDF [${id}]`, { error, reportId: id });
                setShowToast({
                    show: true,
                    message: t('cidCvnCheck:report.downloadErrorMessage'),
                    iconName: 'x-close',
                    variant: 'error',
                });
            } finally {
                setDownloadingIds((prev) => ({ ...prev, [id]: false }));
            }
        },
        [t, vin]
    );

    const handleReportNameClick = useCallback(
        (id: string, reportName: string, result: CidCvnCheckResult) => {
            if (!result) {
                return;
            }
            setSelectedReportHeader({ id, reportName, result });
        },
        [setSelectedReportHeader]
    );

    function handleRunCheckButtonClick() {
        analyticsService.logEvent(ANALYTICS_EVENTS.CID_CVN_CHECK_INITIATED, {
            moduleName: 'cid_cvn',
        });
        googleAnalyticsService.track(GoogleAnalyticsEvents.StartCidCvnCheck);
        if (Array.isArray(accountRetailers) && accountRetailers.length > 1) {
            setModalStep('selectRetailer');
        } else {
            setModalStep('upload');
        }
    }

    const handleViewTechlineCaseButtonClick = useCallback((caseNumber: string, result: CidCvnCheckResult) => {
        if (!caseNumber) {
            return;
        }
        analyticsService.logEvent(ANALYTICS_EVENTS.CID_CVN_RESULT_VIEWED, {
            moduleName: 'cid_cvn',
            resultStatus: result.toLowerCase(),
        });
        window.open(`/techline/${caseNumber}`, '_blank', 'noopener,noreferrer');
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_OPENS_EXISTING_CASE);
    }, []);

    const handleRetryButtonClick = useCallback(
        async (id: string) => {
            if (!vin || !id) {
                return;
            }
            setRetryingIds((prev) => ({ ...prev, [id]: true }));
            const previousRecord = cidCvnList?.find((r) => r.cidCvnStatusId === id);
            const previousResultStatus = previousRecord?.result?.toLowerCase() ?? 'unknown';
            const retryAttemptNumber = (retryCountsById[id] ?? 0) + 1;
            setRetryCountsById((prev) => ({ ...prev, [id]: retryAttemptNumber }));
            analyticsService.logEvent(ANALYTICS_EVENTS.CID_CVN_RETRY_SUBMITTED, {
                moduleName: 'cid_cvn',
                previousResultStatus,
                retryAttemptNumber,
            });
            try {
                const response = await cidCvnService.retryCidCvnCheck(id);

                if (!response.success) {
                    throw response.data;
                }

                const result = response.data?.result;

                if (!result) {
                    throw new Error('No result was found.');
                }
                if (result === CidCvnCheckResult.NoMatchFound) {
                    setShowToast({
                        show: true,
                        message: 'Retry complete. Result remains No Match Found.',
                    });
                } else {
                    await fetchCidCvnData(vin, false);
                    setShowToast({
                        show: true,
                        message: 'Retry complete. The result has been updated.',
                    });
                }
            } catch (error) {
                logger.error(`Failed to retry the CID/CVN check [${id}]`, { error, reportId: id, vin });
                const errorMessage = `An unexpected error occurred during recheck. ${error.message}`;
                setShowToast({
                    show: true,
                    message: errorMessage,
                    iconName: 'x-close',
                    variant: 'error',
                });
            } finally {
                setRetryingIds((prev) => ({ ...prev, [id]: false }));
            }
        },
        [vin, fetchCidCvnData]
    );

    useEffect(() => {
        if (vin) {
            fetchCidCvnData(vin);
        }
    }, [vin]);

    // Not VIN-scoped, so kept out of fetchCidCvnData, which short-circuits once eligible.
    useEffect(() => {
        const abortController = new AbortController();

        (async () => {
            try {
                const response = await cidCvnService.getReferenceDataLastUpdated({
                    signal: abortController.signal,
                });
                if (response.success && response.data) {
                    setReferenceDataLastUpdated(response.data.sourceDataDate ?? null);
                }
            } catch (error) {
                if (!abortController.signal.aborted) {
                    logger.error('Failed to fetch the CID/CVN reference data last updated date', {
                        error,
                    });
                }
            }
        })();

        return () => abortController.abort();
    }, []);

    return (
        <>
            <AlertBar show={cidCvnBlocked} redWarning={cidCvnBlocked} iconHide={true} onClose={() => {}}>
                <Typography variant="default">
                    This feature has been temporarily locked after 3 failed attempts. Please contact for support:
                    techshare_support@subaru.com
                </Typography>
            </AlertBar>
            <div className="pt-7 pl-7 pr-7 d-flex h-100">
                <div className="d-flex w-100 flex-column">
                    <div className="mb-6 d-flex justify-content-between">
                        <div className="d-flex flex-column">
                            <div className="d-flex align-items-center">
                                <Typography variant="h2">{t('cidCvnCheck:title')}</Typography>
                                <OverlayTrigger
                                    key={'info'}
                                    placement="bottom"
                                    overlay={
                                        <Tooltip className={classes.tooltip} id={'info-tool-tip'}>
                                            {t('cidCvnCheck:toolTipMessage')}
                                        </Tooltip>
                                    }
                                >
                                    <Icon name="info" style={{ marginLeft: 15 }} color="black" />
                                </OverlayTrigger>
                            </div>
                            <ReferenceDataLastUpdated sourceDataDate={referenceDataLastUpdated} />
                        </div>
                        {canPerformCidCvnCheck && (
                            <OverlayTrigger
                                key={'run-check-blocked'}
                                placement="bottom"
                                show={cidCvnBlocked ? undefined : false}
                                overlay={
                                    <Tooltip className={classes.supportTooltip} id={'run-check-blocked-tooltip'}>
                                        {t(
                                            'cidCvnCheck:contactSupport',
                                            'Contact Techshare for support: techshare_support@subaru.com'
                                        )}
                                    </Tooltip>
                                }
                            >
                                <span>
                                    <Button
                                        disabled={!cidCvnEligible || cidCvnBlocked}
                                        onPress={handleRunCheckButtonClick}
                                    >
                                        {t('cidCvnCheck:runCheckButton')}
                                    </Button>
                                </span>
                            </OverlayTrigger>
                        )}
                    </div>
                    <CidCvnTable
                        canPerformCidCvnCheck={canPerformCidCvnCheck}
                        canViewTechlineCase={canViewTechlineCase}
                        canViewTechlineDraftCase={canViewTechlineDraftCase}
                        canViewDetailedResults={canViewCidCvnResults}
                        cidCvnEligible={cidCvnEligible}
                        data={cidCvnList}
                        downloadingIds={downloadingIds}
                        retryingIds={retryingIds}
                        isLoading={isLoading}
                        onDownload={handleDownload}
                        onRetry={handleRetryButtonClick}
                        onReportNameClick={handleReportNameClick}
                        onViewCase={handleViewTechlineCaseButtonClick}
                    />
                </div>
            </div>

            {vin && (
                <ScanImageModal
                    onHide={() => setModalStep('')}
                    sessionVin={vin}
                    show={modalStep === 'upload' || modalStep === 'confirm' || modalStep === 'selectRetailer'}
                    uploadedBy={accountFirstandLastName}
                    modalStep={modalStep}
                    modelYear={modelYear}
                    setModalStep={setModalStep}
                />
            )}

            <Modal show={errors.length > 0} centered onHide={clearErrors}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('cidCvnCheck:title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{errors}</Modal.Body>
                <Modal.Footer>
                    <Button onPress={clearErrors}>{t('cidCvnCheck:closeButton')}</Button>
                </Modal.Footer>
            </Modal>

            <ToastNotification
                show={showToast.show}
                onClose={() => setShowToast({ ...showToast, show: false })}
                body={
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <Icon name={showToast.iconName ?? 'checkmark'} size={20} color="white" />
                        <Typography variant="h6" color="white">
                            {showToast.message}
                        </Typography>
                    </div>
                }
                autoHideTime={2000}
                variant={showToast.variant ?? 'success'}
                position="top-center"
            />
        </>
    );
}
